import * as duckdb from '@duckdb/duckdb-wasm';

export class DuckDBService {
  constructor() {
    this.isInitialized = false;
    this.isDataLoaded = false;
    this.db = null;
    this.rawConn = null;
    this.logger = new duckdb.ConsoleLogger();
    this.config = null;

    this.emptyResult = {
      toArray: () => [],
      toString: () => "",
      getChild: () => null,
      schema: [],
      numCols: 0,
      numRows: 0,
    };
    
    this.conn = {
      query: async (queryString) => {
        try {
          if (!this.isInitialized || !this.isDataLoaded || !this.rawConn) {
            return this.emptyResult;
          }
          return await this.rawConn.query(queryString);
        } catch (error) {
          console.error(`Query failed: ${error.message}`);
          return this.emptyResult;
        }
      },
      // Raw query that bypasses isDataLoaded check and doesn't poison state on error
      queryRaw: async (queryString) => {
        if (!this.isInitialized || !this.rawConn) {
          return this.emptyResult;
        }
        return await this.rawConn.query(queryString);
      },
      loadData: async (queryString) => {
        if (!this.isInitialized) {
          console.error('Cannot load data: DuckDB not initialized');
          return false;
        }
    
        try {
          await this.rawConn.query(queryString);
          return true;
        } catch (error) {
          console.error('Error loading data:', error);
          this.isDataLoaded = false;
          return false;
        }
      }
    };
  }

  async initialize({ proxyUrl, apiUrl, scopedToken }) {
    try {
      // Store config for later use
      this.config = { proxyUrl, apiUrl, scopedToken };

      // Use CDN URLs directly - these are served with proper MIME types
      const wasmUrl = 'https://cdn.jsdelivr.net/npm/@duckdb/duckdb-wasm@1.29.0/dist/duckdb-eh.wasm';
      const workerUrl = 'https://cdn.jsdelivr.net/npm/@duckdb/duckdb-wasm@1.29.0/dist/duckdb-browser-eh.worker.js';

      // Fetch worker script and create blob URL to avoid CORS issues
      const workerResponse = await fetch(workerUrl);
      const workerCode = await workerResponse.text();
      const workerBlob = new Blob([workerCode], { type: 'application/javascript' });
      const workerBlobUrl = URL.createObjectURL(workerBlob);

      const MANUAL_BUNDLES = {
        eh: {
          mainModule: wasmUrl,
          mainWorker: workerBlobUrl,
        }
      };

      const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
      const worker = new Worker(bundle.mainWorker);

      //const duckDBConfig = {
      //  maximumThreads: 4, // Adjust the number based on your needs
      //};

      this.db = new duckdb.AsyncDuckDB(this.logger, worker/*, duckDBConfig*/);

      console.log('Instantiating DuckDB...');
      await this.db.instantiate(bundle.mainModule);
      console.log('DuckDB instantiated successfully');

      console.log('Creating DuckDB connection...');
      this.rawConn = await this.db.connect();
      console.log('DuckDB connection created successfully');

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Error initializing DuckDB:', error);
      this.isInitialized = false;
      return false;
    }
  }

  async downloadAndInsertParquet(path, tableName) {
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download parquet: DuckDB not initialized or config missing');
      return false;
    }

    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const url = new URL(`https://${proxyUrl}${path}`);
      url.searchParams.append('url', apiUrl);
      url.searchParams.append('scopedToken', scopedToken);

      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${url} at ${startTime}`);
      const response = await fetch(url, { 
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.apache.parquet'
        }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      await this.db.registerFileBuffer(`${tableName}.parquet`, new Uint8Array(arrayBuffer));
      await this.conn.loadData(`CREATE TABLE IF NOT EXISTS ${tableName} AS SELECT * FROM parquet_scan('${tableName}.parquet')`);
      this.logger.log(`Table ${tableName} created successfully in ${performance.now() - startTime}ms`);
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      return false;
    }
  }

  async downloadAndInsertParquetWithPaging(path, tableName) {
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download parquet: DuckDB not initialized or config missing');
      return false;
    }
  
    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const baseUrl = new URL(`https://${proxyUrl}${path}`);
      baseUrl.searchParams.append('url', apiUrl);
      baseUrl.searchParams.append('scopedToken', scopedToken);
      
      const startTime = performance.now();
      this.logger.log(`Starting paged fetch for ${tableName} at ${startTime}`);
      
      // First create the table structure
      let pageNum = 1;
      const firstPageUrl = new URL(baseUrl.toString());
      firstPageUrl.searchParams.append('page', pageNum);
      
      this.logger.log(`Fetching first page from ${firstPageUrl}`);
      const firstResponse = await fetch(firstPageUrl, { 
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.apache.parquet'
        }
      });
      
      if (!firstResponse.ok) throw new Error(`HTTP ${firstResponse.status}: ${firstResponse.statusText}`);
      
      const firstBlob = await firstResponse.blob();
      if (firstBlob.size === 0) {
        this.logger.log(`No data received for ${tableName}`);
        return false;
      }
      
      const firstArrayBuffer = await firstBlob.arrayBuffer();
      await this.db.registerFileBuffer(`${tableName}_page_${pageNum}.parquet`, new Uint8Array(firstArrayBuffer));
      
      // Create the table with the first page
      await this.conn.loadData(`CREATE TABLE IF NOT EXISTS ${tableName} AS SELECT * FROM parquet_scan('${tableName}_page_${pageNum}.parquet')`);
      this.logger.log(`Table ${tableName} created with page ${pageNum}`);
      
      // Continue loading additional pages
      let hasMoreData = true;
      let totalRowsInserted = 0;
      let rowCount = await this.getRowCount(tableName);
      totalRowsInserted += rowCount;
      
      while (hasMoreData) {
        pageNum++;
        const pageUrl = new URL(baseUrl.toString());
        pageUrl.searchParams.append('page', pageNum);
        
        this.logger.log(`Fetching page ${pageNum} from ${pageUrl}`);
        const response = await fetch(pageUrl, { 
          method: 'GET',
          headers: {
            'Accept': 'application/vnd.apache.parquet'
          }
        });
        
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        
        const blob = await response.blob();
        if (blob.size === 0) {
          this.logger.log(`No more data available after page ${pageNum-1}`);
          hasMoreData = false;
          break;
        }
        
        const arrayBuffer = await blob.arrayBuffer();
        await this.db.registerFileBuffer(`${tableName}_page_${pageNum}.parquet`, new Uint8Array(arrayBuffer));
        
        // Append data to the existing table
        await this.conn.loadData(`INSERT INTO ${tableName} SELECT * FROM parquet_scan('${tableName}_page_${pageNum}.parquet')`);
        
        // Get the new row count to calculate rows inserted
        const newRowCount = await this.getRowCount(tableName);
        const pageRowCount = newRowCount - totalRowsInserted;
        totalRowsInserted = newRowCount;
        
        this.logger.log(`Inserted ${pageRowCount} rows from page ${pageNum}`);
        
        // Free up memory by unregistering the file buffer after insertion
        await this.db.unregisterFileBuffer(`${tableName}_page_${pageNum}.parquet`);
      }
      
      // Unregister the first page file buffer too
      await this.db.unregisterFileBuffer(`${tableName}_page_1.parquet`);
      
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} created successfully with ${totalRowsInserted} total rows in ${totalTime}ms`);
      return true;
    } catch (error) {
      this.logger.log(`Error creating paged table ${tableName} from ${path}: ${error.message}`);
      return false;
    }
  }
  
  // Helper method to get row count
  async getRowCount(tableName) {
    const result = await this.conn.query(`SELECT COUNT(*) as count FROM ${tableName}`);
    return result.get(0).count;
  }

  async downloadAndInsertArrow(path, tableName) {
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download Arrow file: DuckDB not initialized or config missing');
      return false;
    }
  
    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const url = new URL(`https://${proxyUrl}${path}`);
      url.searchParams.append('url', apiUrl);
      url.searchParams.append('scopedToken', scopedToken);
  
      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${url} at ${startTime}`);
  
      // Stream the response if the browser supports it
      const response = await fetch(url, { method: 'GET' });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      
      // Define EOS signal according to the Arrow IPC streaming format
      const EOS = new Uint8Array([255, 255, 255, 255, 0, 0, 0, 0]);
      
      if (response.body && response.body.getReader) {
        // Stream processing for better memory efficiency
        const reader = response.body.getReader();
        const inserts = [];
        
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          inserts.push(this.rawConn.insertArrowFromIPCStream(value, { name: tableName }));
        }
        
        // Write the End-of-Stream marker
        inserts.push(this.rawConn.insertArrowFromIPCStream(EOS, { name: tableName }));
        await Promise.all(inserts);
      } else {
        // Fallback for browsers that don't support streaming
        const arrayBuffer = await response.arrayBuffer();
        await this.rawConn.insertArrowFromIPCStream(new Uint8Array(arrayBuffer), { name: tableName });
        await this.rawConn.insertArrowFromIPCStream(EOS, { name: tableName });
      }
      
      this.logger.log(`Table ${tableName} created successfully in ${performance.now() - startTime}ms`);
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      return false;
    }
  }

  async downloadAndInsertJSON(path, tableName) {
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download JSON: DuckDB not initialized or config missing');
      return false;
    }

    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const url = new URL(`https://${proxyUrl}${path}`);
      url.searchParams.append('url', apiUrl);
      url.searchParams.append('scopedToken', scopedToken);

      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${url} at ${startTime}`);
      const response = await fetch(url, { method: 'GET' });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      
      const jsonData = await response.json();
      const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
      
      if (dataArray.length === 0) {
        throw new Error('No data received');
      }

      const jsonString = JSON.stringify(dataArray);
      const encoder = new TextEncoder();
      const jsonBuffer = encoder.encode(jsonString);
      
      await this.db.registerFileBuffer(`${tableName}.json`, jsonBuffer);
      
      await this.conn.loadData(`
        CREATE TABLE IF NOT EXISTS ${tableName} AS 
        SELECT * FROM read_json_auto('${tableName}.json')
      `);
      
      this.logger.log(`Table ${tableName} created successfully in ${performance.now() - startTime}ms`);
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      return false;
    }
  }

  async logQuery(query) {
    try {
      const result = await this.conn.query(query);
      
      const data = [];
      for (let row of result) {
        data.push({...row});
      }

      console.table(data);
      return data;
    } catch (error) {
      this.logger.log(`Error executing query "${query}": ${error.message}`);
      return [];
    }
  }

  async showSchema(tableName) {
    try {
      const result = await this.conn.query(`
        SELECT 
          column_name,
          data_type,
          is_nullable
        FROM information_schema.columns 
        WHERE table_name = '${tableName}'
      `);
      
      const data = result.toArray();
      
      console.log("Data to display:", data); // Check the actual data
      console.table(data);
      
      return data;
    } catch (error) {
      console.error(`Error getting schema for table ${tableName}:`, error);
      return [];
    }
  }
}